<template>
  <div :class="['message', message.type]">
    <template v-if="message.type === 'ai'">
      <div class="ai-message-body">
        <div v-if="thinkingTime > 0 && !hideLoadingDots" class="loading-dots">
          <span></span>
          <span></span>
          <span></span>
        </div>
        <div 
          v-if="activeReasoningText.trim()"
          class="reasoning"
          :class="{ collapsed }"
        >
          <div class="reasoning-header">
            <span class="thinking-time">
              <span v-if="isShowingReasoningPreview" class="reasoning-live-dot"></span>
              {{ isShowingReasoningPreview ? '思考中' : `思考 ${message.thinkingTime || thinkingTime}秒` }}
            </span>
            <button class="toggle-button" @click="toggleCollapse">
              <svg class="toggle-icon" :class="{ collapsed }" viewBox="0 0 24 24" width="16" height="16">
                <path fill="currentColor" d="M7.41 8.59L12 13.17l4.59-4.58L18 10l-6 6-6-6 1.41-1.41z"/>
              </svg>
            </button>
          </div>
          <div class="reasoning-content markdown-body" v-html="renderedReasoningContent"></div>
        </div>
        <div
          v-if="showContentWrapper"
          class="content-wrapper"
          :class="{ 'is-streaming': isStreaming }"
        >
          <div class="content markdown-body" v-html="renderedContent"></div>
          <ChatPanel
            v-if="message.tejiaoUi?.version === 1 && !isStreaming"
            :key="message.serverId || message.id"
            :ui="message.tejiaoUi"
            :saved-state="message.tejiaoUiState"
            :message-id="message.serverId || message.id"
            :disabled="interactionDisabled"
            @submit="emit('ui-submit', $event)"
            @state="emit('ui-state', { message, state: $event })"
          />

          <div v-if="displayContent.trim() && !thinkingTime && !isStreaming" class="message-actions desktop-actions">
            <div class="action-wrap copy-action" :class="{ 'is-open': showCopyMenu }">
              <button class="message-action-button" type="button" data-tooltip="复制" @click="showCopyMenu = !showCopyMenu">
                <svg viewBox="0 0 24 24" width="16" height="16">
                  <path fill="currentColor" d="M16 1H4a2 2 0 0 0-2 2v12h2V3h12zm3 4H8a2 2 0 0 0-2 2v14h13a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2m0 14H8V7h11z"/>
                </svg>
              </button>
              <div class="copy-menu">
                <button type="button" @click="copyPlainText">复制为纯文字</button>
                <button type="button" @click="copyWithFormat">复制为 Markdown</button>
              </div>
            </div>

            <button class="message-action-button" type="button" data-tooltip="重新生成" @click="handleRegenerate">
              <svg viewBox="0 0 24 24" width="16" height="16">
                <path fill="currentColor" d="M17.65 6.35A7.95 7.95 0 0 0 12 4a8 8 0 1 0 7.45 5.08h-2.16A6 6 0 1 1 12 6c1.66 0 3.14.69 4.22 1.78L13 11h8V3z"/>
              </svg>
            </button>

            <button class="message-action-button" type="button" data-tooltip="导出" @click="exportToWord">
              <svg viewBox="0 0 24 24" width="16" height="16">
                <path fill="currentColor" d="M5 20h14v-2H5zm7-16L7 9h3v6h4V9h3z"/>
              </svg>
            </button>
          </div>

          <div v-if="displayContent.trim() && !thinkingTime && !isStreaming" class="mobile-actions-inline">
            <button type="button" @click="copyWithFormat">
              <svg viewBox="0 0 24 24" width="18" height="18">
                <path fill="currentColor" d="M16 1H4a2 2 0 0 0-2 2v12h2V3h12zm3 4H8a2 2 0 0 0-2 2v14h13a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2m0 14H8V7h11z"/>
              </svg>
              复制
            </button>
            <button type="button" @click="handleRegenerate">
              <svg viewBox="0 0 24 24" width="18" height="18">
                <path fill="currentColor" d="M17.65 6.35A7.95 7.95 0 0 0 12 4a8 8 0 1 0 7.45 5.08h-2.16A6 6 0 1 1 12 6c1.66 0 3.14.69 4.22 1.78L13 11h8V3z"/>
              </svg>
              重新生成
            </button>
            <button type="button" @click="exportToWord">
              <svg viewBox="0 0 24 24" width="18" height="18">
                <path fill="currentColor" d="M5 20h14v-2H5zm7-16L7 9h3v6h4V9h3z"/>
              </svg>
              导出文档
            </button>
          </div>
        </div>
      </div>
    </template>
    <template v-else>
      <div class="content">
        <div v-if="message.images?.length" class="message-images">
          <img v-for="(url, index) in message.images" :key="index" :src="url" :alt="`已上传图片 ${index + 1}`" />
        </div>
        {{ message.content }}
      </div>
    </template>
    <Toast ref="toast" />
  </div>
</template>

<script setup>
import { ref, computed, onUnmounted, watch } from 'vue';
import { renderMarkdown } from '../utils/markdownConfig';
import { stripModelProtocolMarkers } from '../utils/modelOutput';
import { Document, Packer, Paragraph, TextRun } from 'docx';
import { saveAs } from 'file-saver';
import Toast from './Toast.vue';
import ChatPanel from './ChatPanel.vue';
import { panelExportText } from '../utils/chatPanel';

const props = defineProps({
  message: {
    type: Object,
    required: true
  },
  thinkingTime: {
    type: Number,
    default: 0
  },
  isStreaming: {
    type: Boolean,
    default: false
  },
  interactionDisabled: Boolean,
  hideLoadingDots: {
    type: Boolean,
    default: false
  }
});

const emit = defineEmits(['regenerate', 'ui-submit', 'ui-state']);

// Toast组件引用
const toast = ref(null);

// 默认展开状态
const collapsed = ref(false);
const showCopyMenu = ref(false);

const displayContent = computed(() => stripModelProtocolMarkers(props.message.content || ''));

const exportContent = computed(() => [displayContent.value, panelExportText(props.message.tejiaoUi, props.message.tejiaoUiState)].filter(Boolean).join('\n\n'));

const showContentWrapper = computed(() => {
  return props.message.tejiaoUi?.version === 1 || displayContent.value.trim() || (props.isStreaming && props.hideLoadingDots && !isShowingReasoningPreview.value);
});

const renderedContent = computed(() => {
  if (props.message.type === 'user') return displayContent.value;
  return renderMarkdown(displayContent.value);
});

const isShowingReasoningPreview = computed(() => {
  return props.message.type === 'ai' && !!props.message.isReasoningPreview;
});

const activeReasoningText = computed(() => {
  if (isShowingReasoningPreview.value) {
    return stripModelProtocolMarkers(props.message.reasoningPreview || '');
  }
  return stripModelProtocolMarkers(props.message.reasoning || '');
});

const renderedReasoningContent = computed(() => {
  return renderMarkdown(activeReasoningText.value || '');
});

// 监听思考过程的变化
let hasAutoCollapsedReasoning = false;

watch(() => [props.message.reasoning, props.message.content, props.message.isReasoningPreview, props.isStreaming], ([reasoning, content, isPreview, streaming]) => {
  // 只在思考过程完全结束时自动折叠
  if (reasoning && content && !isPreview && !streaming && !hasAutoCollapsedReasoning) {
    hasAutoCollapsedReasoning = true;
    // 思考过程已完成且回复内容开始显示时，延迟折叠
    setTimeout(() => {
      collapsed.value = true;
    }, 1000);
  }
}, { immediate: true });

const toggleCollapse = () => {
  collapsed.value = !collapsed.value;
};

const closeCopyMenu = () => {
  showCopyMenu.value = false;
};

// 将Markdown转换为纯文本的工具函数
const removeMarkdown = (text) => {
  if (!text) return '';
  
  return text
    .replace(/#{1,6}\s/g, '') // 移除标题标记
    .replace(/\*\*/g, '') // 移除粗体标记
    .replace(/\*/g, '') // 移除斜体标记
    .replace(/`{1,3}/g, '') // 移除代码标记
    .replace(/\[([^\]]+)\]\([^\)]+\)/g, '$1') // 替换链接为纯文本
    .replace(/!\[([^\]]+)\]\([^\)]+\)/g, '') // 移除图片
    .replace(/^>\s/gm, '') // 移除引用标记
    .replace(/^[0-9]+\.\s/gm, '') // 移除有序列表标记
    .replace(/^-\s/gm, '') // 移除无序列表标记
    .replace(/\n{2,}/g, '\n\n'); // 规范化多余的换行符
};

// 导出为Word文档
const exportToWord = async () => {
  try {
    // 创建段落数组
    const children = [];
    
    // 先将Markdown内容转换为纯文本
    const plainText = removeMarkdown(exportContent.value);
    
    // 处理文本内容，将纯文本分割为段落
    const paragraphs = plainText.split('\n\n').filter(p => p.trim());
    
    // 将每个段落添加到文档中
    paragraphs.forEach(paragraph => {
      if (!paragraph.trim()) return;
      
      children.push(new Paragraph({
        children: [new TextRun(paragraph)],
        spacing: { after: 200 }
      }));
    });
    
    // 创建Word文档对象，添加必要的文档属性和内容
    const doc = new Document({
      creator: "特教求索",
      title: "AI回复内容",
      description: "由特教求索 AI 生成的回复",
      sections: [{
        properties: {},
        children: children
      }]
    });
    
    // 生成Word文档的二进制数据
    const buffer = await Packer.toBlob(doc);
    
    // 使用file-saver库保存文件
    saveAs(buffer, `ai-response-${new Date().toISOString().slice(0,10)}.docx`);
    
    // 显示成功提示
    toast.value?.showToast({
      message: 'Word文档已下载',
      type: 'success'
    });
    
    closeCopyMenu();
  } catch (error) {
    console.error('导出Word文档失败:', error);
    toast.value?.showToast({
      message: '导出Word文档失败，请重试',
      type: 'error'
    });
  }
};

// 复制带格式的文本
const copyWithFormat = async () => {
  try {
    await navigator.clipboard.writeText(exportContent.value);
    
    toast.value?.showToast({
      message: '已复制Markdown格式文本到剪贴板',
      type: 'success'
    });
    closeCopyMenu();
  } catch (error) {
    console.error('复制失败:', error);
    toast.value?.showToast({
      message: '复制失败，请重试',
      type: 'error'
    });
  }
};

// 复制纯文本（去除Markdown格式）
const copyPlainText = async () => {
  try {
    // 简单的Markdown格式移除
    const plainText = removeMarkdown(exportContent.value);
    
    await navigator.clipboard.writeText(plainText);
    
    toast.value?.showToast({
      message: '已复制纯文本内容到剪贴板',
      type: 'success'
    });
    closeCopyMenu();
  } catch (error) {
    console.error('复制失败:', error);
    toast.value?.showToast({
      message: '复制失败，请重试',
      type: 'error'
    });
  }
};

const handleRegenerate = () => {
  closeCopyMenu();
  emit('regenerate', props.message);
};

watch(() => showCopyMenu.value, (newVal) => {
  if (newVal) {
    setTimeout(() => {
      window.addEventListener('click', handleOutsideClick);
    }, 0);
  } else {
    window.removeEventListener('click', handleOutsideClick);
  }
});

const handleOutsideClick = (event) => {
  const copyAction = event.target.closest('.copy-action');
  if (!copyAction) {
    closeCopyMenu();
  }
};

onUnmounted(() => {
  if (typeof window !== 'undefined') {
    window.removeEventListener('click', handleOutsideClick);
  }
});
</script>

<style lang="scss" scoped>
@import '../styles/variables.scss';

.message-images {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 8px;

  img {
    width: auto;
    max-width: min(100%, 280px);
    max-height: 220px;
    object-fit: contain;
    border-radius: 8px;
  }
}

.content-wrapper {
  position: relative;
  display: block;
  width: 100%;
  max-width: 100%;
}

@keyframes reasoningPulse {
  0% {
    box-shadow: 0 0 0 0 rgba($primary-color, 0.36);
  }
  70% {
    box-shadow: 0 0 0 7px rgba($primary-color, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba($primary-color, 0);
  }
}

.message-actions {
  position: absolute;
  right: 12px;
  bottom: 10px;
  display: flex;
  align-items: center;
  gap: 6px;
  z-index: 3;
}

.action-wrap {
  position: relative;
}

.message-action-button {
  position: relative;
  width: 30px;
  height: 30px;
  border: 1px solid rgba(148, 163, 184, 0.22);
  border-radius: 8px;
  background: #ffffff;
  color: #667085;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.18s ease, border-color 0.18s ease, color 0.18s ease, transform 0.18s ease;

  &:hover {
    background: #f5f9ff;
    border-color: rgba($primary-color, 0.28);
    color: $primary-color;
    transform: translateY(-1px);
  }

  &::after {
    content: attr(data-tooltip);
    position: absolute;
    bottom: calc(100% + 8px);
    left: 50%;
    transform: translateX(-50%) translateY(4px);
    white-space: nowrap;
    border-radius: 6px;
    background: rgba(15, 23, 42, 0.92);
    color: #fff;
    font-size: 0.76rem;
    line-height: 1;
    padding: 6px 8px;
    opacity: 0;
    pointer-events: none;
    transition: opacity 0.16s ease, transform 0.16s ease;
  }

  &:hover::after {
    opacity: 1;
    transform: translateX(-50%) translateY(0);
  }
}

.copy-menu {
  position: absolute;
  right: 0;
  bottom: calc(100% - 1px);
  background-color: white;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
  box-shadow: 0 12px 28px rgba(15, 23, 42, 0.12);
  z-index: 12;
  overflow: hidden;
  min-width: 156px;
  opacity: 0;
  pointer-events: none;
  transform: translateY(4px);
  transition: opacity 0.16s ease, transform 0.16s ease;
  
  button {
    display: block;
    width: 100%;
    text-align: left;
    padding: 8px 12px;
    border: none;
    background-color: transparent;
    cursor: pointer;
    color: $text-color;
    font-size: 0.9rem;
    
    &:hover {
      background-color: #f8fafc;
    }
    
    &:not(:last-child) {
      border-bottom: 1px solid rgba($border-color, 0.5);
    }
  }
}

.copy-action:hover .copy-menu,
.copy-action:focus-within .copy-menu,
.copy-action.is-open .copy-menu {
  opacity: 1;
  pointer-events: auto;
  transform: translateY(0);
}

.mobile-action-mask {
  display: none;
}

.message {
  margin-bottom: 20px;
  
  &.user {
    margin-left: auto;
    text-align: right;
    
    .content {
      display: inline-block;
      text-align: left;
      max-width: min(74%, 720px);
      background-color: #255da8;
      color: $user-msg-text;
      border-radius: 8px 8px 2px 8px;
      padding: 12px 15px;
      box-shadow: 0 8px 18px rgba(37, 93, 168, 0.16);
      line-height: 1.65;
    }
  }

  &.ai {
    margin-right: auto;
    width: 100%;
    max-width: 56rem;

    .ai-message-body {
      flex: 1;
      min-width: 0;
    }

    .content-wrapper {
      background: #ffffff;
      border: 1px solid rgba(203, 213, 225, 0.86);
      border-radius: 14px;
      box-shadow: 0 12px 34px rgba(15, 23, 42, 0.06);
      padding: 18px 20px 56px;
      position: relative;

      @media (max-width: 768px) {
        border-radius: 12px;
        padding: 14px 14px 64px;
      }
    }
    
    .content {
      display: block;
      width: 100%;
      background-color: transparent;
      color: $ai-msg-text;
      border-radius: 0;
      border: 0;
      padding: 0;
      box-shadow: none;
      position: relative;
      user-select: text;
      -webkit-user-select: text;
      -webkit-touch-callout: default;

      &::before {
        display: none;
      }
    }
  }
}

.mobile-actions-inline {
  display: none;
}

@media (max-width: 768px) {
  .desktop-actions {
    display: none;
  }

  .mobile-actions-inline {
    position: absolute;
    right: 10px;
    bottom: 10px;
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 6px;
    border-radius: 999px;
    border: 1px solid rgba(203, 213, 225, 0.85);
    background: rgba(248, 250, 252, 0.96);
    box-shadow: 0 8px 22px rgba(15, 23, 42, 0.08);

    button {
      min-height: 32px;
      border: 0;
      border-radius: 999px;
      background: #ffffff;
      color: #255da8;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 5px;
      padding: 0 9px;
      font-size: 0.78rem;
      font-weight: 600;
      box-shadow: 0 1px 3px rgba(15, 23, 42, 0.06);
    }
  }
}

.reasoning {
  background-color: #f7f9fc;
  border: 1px solid #e4ebf3;
  border-radius: 8px;
  margin-bottom: 12px;
  overflow: hidden;
  transition: all 0.3s ease;

  .reasoning-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 8px 12px;
    border-bottom: 1px solid rgba(203, 213, 225, 0.55);
  }

  .thinking-time {
    display: inline-flex;
    align-items: center;
    gap: 7px;
    font-size: 0.86em;
    color: #5d7086;
  }

  .reasoning-live-dot {
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background-color: $primary-color;
    animation: reasoningPulse 1.4s ease-out infinite;
  }

  .toggle-button {
    padding: $spacing-xs;
    border: none;
    background-color: transparent;
    color: $primary-color;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    width: 24px;
    height: 24px;

    &:hover {
      background-color: rgba($primary-color, 0.1);
    }

    .toggle-icon {
      transition: transform 0.3s ease;
      
      &.collapsed {
        transform: rotate(-180deg);
      }
    }
  }

  .reasoning-content {
    padding: 12px;
    color: #5d7086;
    font-size: 0.9em;
    transition: all 0.3s ease;
    max-height: none;
    line-height: 1.6;

    :deep(p),
    :deep(li),
    :deep(blockquote),
    :deep(td),
    :deep(th) {
      color: $thinking-text;
    }

    :deep(p) {
      margin: 0.7em 0;

      &:first-child {
        margin-top: 0;
      }

      &:last-child {
        margin-bottom: 0;
      }
    }

    :deep(ul),
    :deep(ol) {
      margin: 0.7em 0;
      padding-left: 1.6em;
    }

    :deep(pre) {
      background-color: #eaf1f8;
      border-color: #d7e2ee;
    }
  }

  &.collapsed .reasoning-content {
    max-height: 0;
    padding-top: 0;
    padding-bottom: 0;
    overflow: hidden;
  }
}

.content {
  padding: $spacing-md;
  word-wrap: break-word;
}

.markdown-body {
  font-size: 1rem;
  line-height: 1.85;
  overflow-wrap: anywhere;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif;
  color: #22364d;
  user-select: text;
  -webkit-user-select: text;
  -webkit-touch-callout: default;
}

.markdown-body :deep(a[href]) {
  color: #075985;
  font-weight: 500;
  text-decoration: underline;
  text-decoration-thickness: 1.5px;
  text-underline-offset: 3px;
  overflow-wrap: anywhere;
}

.markdown-body :deep(a[href]:hover) {
  color: #0369a1;
  background: rgba(14, 165, 233, 0.18);
}

.markdown-body :deep(p) {
  margin: 1em 0;
  color: $text-color;
  text-wrap: pretty;
}

.markdown-body :deep(p:first-child),
.markdown-body :deep(h1:first-child),
.markdown-body :deep(h2:first-child),
.markdown-body :deep(h3:first-child),
.markdown-body :deep(h4:first-child),
.markdown-body :deep(h5:first-child),
.markdown-body :deep(h6:first-child) {
  margin-top: 0;
}

.markdown-body :deep(p:last-child) {
  margin-bottom: 0;
}

.markdown-body :deep(h1),
.markdown-body :deep(h2),
.markdown-body :deep(h3),
.markdown-body :deep(h4),
.markdown-body :deep(h5),
.markdown-body :deep(h6) {
  margin: 1.6em 0 0.65em;
  font-weight: 700;
  line-height: 1.45;
  color: #172b42;
  text-wrap: balance;
}

.markdown-body :deep(h1) {
  font-size: 1.75em;
}

.markdown-body :deep(h2) {
  font-size: 1.5em;
}

.markdown-body :deep(h3) {
  font-size: 1.25em;
}

.markdown-body :deep(h4) {
  font-size: 1.125em;
}

.markdown-body :deep(h5),
.markdown-body :deep(h6) {
  font-size: 1em;
}

.markdown-body :deep(strong) {
  font-weight: 700;
}

.markdown-body :deep(em) {
  font-style: italic;
}

.markdown-body :deep(hr) {
  margin: 2em 0;
  border: 0;
  border-top: 1px solid #e2e8f0;
}

.markdown-body :deep(ul),
.markdown-body :deep(ol) {
  margin: 1em 0;
  padding-left: 1.5em;
}

.markdown-body :deep(ul) {
  list-style: disc outside;
}

.markdown-body :deep(ol) {
  list-style: decimal outside;
}

.markdown-body :deep(ul ul) {
  list-style: circle outside;
}

.markdown-body :deep(ul ul ul) {
  list-style: square outside;
}

.markdown-body :deep(li) {
  margin: 0.5em 0;
  padding-left: 0.15em;
}

.markdown-body :deep(li > p) {
  margin: 0.4em 0;
}

.markdown-body :deep(li > ul),
.markdown-body :deep(li > ol) {
  margin: 0.4em 0;
}

.markdown-body :deep(li > input[type='checkbox']) {
  width: 14px;
  height: 14px;
  margin: 0 7px 0 0;
  vertical-align: -2px;
  accent-color: #2b5fb4;
}

.markdown-body :deep(code) {
  font-family: ui-monospace, SFMono-Regular, SF Mono, Menlo, Consolas, Liberation Mono, monospace;
  padding: 0.2em 0.4em;
  font-size: 0.9em;
  background-color: #f1f5f9;
  border-radius: 3px;
  color: #334155;
}

.markdown-body :deep(pre) {
  margin: 1em 0;
  padding: 1em;
  background-color: #f8fafc;
  border-radius: $border-radius-sm;
  border: 1px solid #e2e8f0;
  overflow-x: auto;
}

.markdown-body :deep(pre code) {
  padding: 0;
  background-color: transparent;
  color: #334155;
}

.markdown-body :deep(blockquote) {
  margin: 1em 0;
  padding: 0.5em 1em;
  color: #475569;
  border-left: 3px solid #cbd5e1;
  background-color: #f8fafc;
  border-radius: 0 $border-radius-sm $border-radius-sm 0;
}

.markdown-body :deep(.markdown-table) {
  max-width: 100%;
  margin: 1.25em 0;
  overflow-x: auto;
}

.markdown-body :deep(.markdown-table:focus-visible),
.markdown-body :deep(a[href]:focus-visible) {
  outline: 2px solid $primary-color;
  outline-offset: 3px;
}

.markdown-body :deep(table) {
  width: 100%;
  min-width: 28rem;
  border-collapse: collapse;
  font-size: 0.95em;
  line-height: 1.65;
}

.markdown-body :deep(th),
.markdown-body :deep(td) {
  padding: 12px 14px;
  border-bottom: 1px solid #e2e8f0;
  text-align: left;
  vertical-align: top;
}

.markdown-body :deep(th) {
  background-color: #f8fafc;
  font-weight: 700;
}

.markdown-body :deep([align="right"]) {
  text-align: right;
}

.markdown-body :deep([align="center"]) {
  text-align: center;
}

@media (max-width: 768px) {
  .markdown-body {
    line-height: 1.8;
  }

  .markdown-body :deep(ul),
  .markdown-body :deep(ol) {
    padding-left: 1.2em;
  }

  .markdown-body :deep(li) {
    margin: 0.35em 0;
  }

  .markdown-body :deep(ul ul),
  .markdown-body :deep(ol ol) {
    padding-left: 0.9em;
    margin-left: 0.25em;
    border-left: 2px solid #e2e8f0;
    padding-top: 0.2em;
  }
}

.loading-dots {
  display: inline-flex;
  align-items: center;
  padding: $spacing-sm $spacing-md;
  background-color: $thinking-bg;
  border-radius: $border-radius-md;
  gap: 4px;
  
  span {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background-color: $primary-color;
    opacity: 0.6;
    animation: dot-flashing 1s infinite alternate;
    
    &:nth-child(2) {
      animation-delay: 0.2s;
    }
    
    &:nth-child(3) {
      animation-delay: 0.4s;
    }
  }
}

@keyframes dot-flashing {
  0% {
    opacity: 0.2;
    transform: scale(0.8);
  }
  100% {
    opacity: 0.8;
    transform: scale(1);
  }
}
</style> 
